/**
 * G.2 — Hydrate asset bytes for vault export.
 *
 * F7 (vault export) builds a PortableSite from in-memory notes + flows
 * but couldn't include binary assets because the SPA only had row
 * metadata, not bytes. G.2 closes that gap: given the vault's asset
 * rows from Convex + a resolver that produces a download URL per row,
 * this module fetches each asset and returns PortableAsset[] ready
 * for `toFolder` / `toZip` / `toSharePointTree` projections.
 *
 * Fetches run in parallel with a small concurrency cap so a vault with
 * 200 assets doesn't open 200 concurrent HTTP requests.
 */

import type { PortableAsset } from '../../portable-site';

export interface AssetRowInput {
  /** Convex row id (`_id`) — passed back through urlForAsset. */
  id: string;
  path: string;
  /** Lowercased extension without the dot, e.g. 'png'. */
  ext?: string;
  mimeType?: string;
  sizeBytes?: number;
  lastModified?: string;
}

export interface HydrateOpts {
  /**
   * Resolve a row id to a download URL. Returns null when the asset has
   * no underlying storageId (placeholder rows from earlier imports).
   * Implementations should use Convex's `vaults.getAssetDownloadUrl`.
   */
  urlForAsset: (assetId: string) => Promise<string | null>;
  /** Max in-flight fetches at once. Default 6. */
  concurrency?: number;
}

export interface HydrateResult {
  assets: PortableAsset[];
  /** Rows skipped because no URL could be resolved. */
  skipped: Array<{ id: string; path: string; reason: string }>;
}

function extFromPath(path: string): string {
  const dot = path.lastIndexOf('.');
  return dot > 0 ? path.slice(dot + 1).toLowerCase() : '';
}

function lastModifiedMs(iso?: string): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : undefined;
}

export async function hydrateAssetBytes(
  rows: ReadonlyArray<AssetRowInput>,
  opts: HydrateOpts,
): Promise<HydrateResult> {
  const concurrency = Math.max(1, opts.concurrency ?? 6);
  const queue = rows.slice();
  const out: PortableAsset[] = [];
  const skipped: HydrateResult['skipped'] = [];

  async function fetchOne(row: AssetRowInput): Promise<void> {
    try {
      const url = await opts.urlForAsset(row.id);
      if (!url) {
        skipped.push({ id: row.id, path: row.path, reason: 'no storageId' });
        return;
      }
      const res = await fetch(url);
      if (!res.ok) {
        skipped.push({ id: row.id, path: row.path, reason: `HTTP ${res.status}` });
        return;
      }
      const buf = await res.arrayBuffer();
      const bytes = new Uint8Array(buf);
      const asset: PortableAsset = {
        path: row.path,
        ext: row.ext ?? extFromPath(row.path),
        bytes,
        sizeBytes: row.sizeBytes ?? bytes.byteLength,
      };
      if (row.mimeType) asset.mimeType = row.mimeType;
      const lm = lastModifiedMs(row.lastModified);
      if (lm !== undefined) asset.lastModifiedMs = lm;
      out.push(asset);
    } catch (err) {
      skipped.push({
        id: row.id,
        path: row.path,
        reason: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const workers: Promise<void>[] = [];
  for (let i = 0; i < Math.min(concurrency, queue.length); i++) {
    workers.push(
      (async function worker() {
        while (queue.length > 0) {
          const row = queue.shift();
          if (!row) return;
          await fetchOne(row);
        }
      })(),
    );
  }
  await Promise.all(workers);
  // Preserve input order for deterministic exports.
  const order = new Map(rows.map((r, i) => [r.id, i] as const));
  out.sort((a, b) => {
    const ai = order.get((rows.find((r) => r.path === a.path) ?? rows[0])!.id) ?? 0;
    const bi = order.get((rows.find((r) => r.path === b.path) ?? rows[0])!.id) ?? 0;
    return ai - bi;
  });
  return { assets: out, skipped };
}
